import React, { Component } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import Link from "../../../components/LinkWithCountry/LinkWithCountry";
import { isRecaptcha } from "../../../shared/utilities/config";
import {
  setCaptchaToken
} from "../../../shared/utilities/captcha";

import * as userRepo from "../../../shared/repos/graphql/user";
import {
  mapLoginOtpChallengeProps,
  shouldUseLoginOtpChallenge,
} from "../../../shared/utilities/loginOtpChallenge";
import LoginOtpChallenge from "./LoginOtpChallenge";

// FORMS
import {
  handleFormInput,
  getValidationErrorMessagesFromApiError,
  isValidEmail,
} from "../../../shared/utilities/validations";
import * as tagmanagerEvents from '../../../shared/utilities/tagmanagerEvents';
import {
  amplitudeSignInMethodByLoginType,
  defaultLabels,
  loginType,
} from "../../../shared/constants/login";
import Button from "../../../components/Button/Button";
import SimpleInput from "../../../components/Input/SimpleInput";

import styles from "./Login.module.css";
import captchaAction from "../../../shared/constants/captchaAction";
import * as elementsActions from "../../../redux/actions/elements";
import amplitudeAnalytics from "../../../shared/utilities/amplitude";

const usernameIcon = require("./imgs/username.svg");
const passwordIcon = require("./imgs/password.svg");

export const isBasicEmailFormat = isValidEmail;

export class LoginWithEmail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      username: "",
      password: "",
      usernameErrorMessage: null,
      passwordErrorMessage: null,
      responseErrorMessage: null,
      loading: false,
      showOtpChallenge: false,
      emailOtpChallenge: null,
      otpCustomer: null,
    };
  }

  handleSubmit = async () => {
    const { username, password } = this.state;
    const {
      handleLogin,
      setVerificationToken,
      setVerificationAction,
      passwordlessEmailLoginEnabled
    } = this.props;
    const passwordForLogin = passwordlessEmailLoginEnabled ? null : password;
    this.setState({
      usernameErrorMessage: null,
      passwordErrorMessage: null,
      responseErrorMessage: null
    });
    if (!username || !isValidEmail(username)) {
      document.getElementById('email_address').focus();
      return this.setState({
        usernameErrorMessage:
          "Please enter email address in format: yourname@example.com"
      });
    }
    tagmanagerEvents.beginSignIn(loginType.email, {username});
    if (!passwordlessEmailLoginEnabled && !password) {
      document.getElementById('password').focus();
      return this.setState({
        passwordErrorMessage: "Please enter a Password! "
      });
    }
    this.setState({
      loading: true
    });

    if (isRecaptcha()) {
      await setCaptchaToken(setVerificationToken, setVerificationAction, captchaAction.loginWithEmail);
    }

    const response = await userRepo
      .login(username, passwordForLogin, null, null, null)
      .catch(error => {
        this.setState({ loading: false });
        this.handleLoginError(error);
        return null;
      });

    if (response && response.data) {
      const { login } = response.data;
      const { token, customer } = login;

      if (shouldUseLoginOtpChallenge(login)) {
        this.setState({
          showOtpChallenge: true,
          emailOtpChallenge: login.loginOtpChallenge,
          otpCustomer: customer,
          password: passwordForLogin,
          loading: false,
          usernameErrorMessage: "",
          passwordErrorMessage: "",
          responseErrorMessage: ""
        });
        return null;
      }

      // Existing success flow (backward compatible)
      await handleLogin(token, customer, passwordForLogin, {
        phoneVerificationRequired: login.phoneVerificationRequired,
        loginOtpChallenge: login.loginOtpChallenge,
      });
      amplitudeAnalytics.trackSignIn(this.buildEmailSignInEvent(true, customer));
    } else {
      amplitudeAnalytics.trackSignIn(this.buildEmailSignInEvent(false));
    }
    this.setState({
      loading: false
    });
    return null;
  };

  handleLoginError = error => {
    const validationErrors = getValidationErrorMessagesFromApiError(error);

    // API validation errors only apply to the email field.
    if (validationErrors.length) {
      this.setState({
        usernameErrorMessage: validationErrors
          .map(({ message }) => message)
          .join("\n"),
        passwordErrorMessage: null,
        responseErrorMessage: null,
      });
      return;
    }

    // Direct error message: show as a single response banner.
    this.setState({
      usernameErrorMessage: null,
      passwordErrorMessage: null,
      responseErrorMessage: error.message,
    });
  };

  buildEmailSignInEvent = (isAccepted, customer) => {
    const { passwordlessEmailLoginEnabled } = this.props;
    const event = {
      signInMethod: amplitudeSignInMethodByLoginType[loginType.email],
      isAccepted,
      isPasswordless: passwordlessEmailLoginEnabled,
    };
    if (customer?.id != null) {
      event.customerId = customer.id;
    }
    return event;
  };

  handleOtpSuccess = async (token, customer, phoneVerificationContext = {}) => {
    const { password } = this.state;
    const { handleLogin } = this.props;

    await handleLogin(token, customer, password, phoneVerificationContext);
    amplitudeAnalytics.trackSignIn(this.buildEmailSignInEvent(true, customer));
  };

  handleOtpFailure = () => {
    amplitudeAnalytics.trackSignIn(this.buildEmailSignInEvent(false));
  };

  render() {
    const {
      username,
      password,
      usernameErrorMessage,
      passwordErrorMessage,
      responseErrorMessage,
      loading,
      showOtpChallenge,
      emailOtpChallenge,
      otpCustomer
    } = this.state;
    const { toggleLogin, passwordlessEmailLoginEnabled } = this.props;

    // Show OTP Challenge if required
    if (showOtpChallenge && emailOtpChallenge) {
      const otpChallengeProps = mapLoginOtpChallengeProps(emailOtpChallenge, otpCustomer, {
        hideTrustDeviceOption: passwordlessEmailLoginEnabled,
      });

      return (
        <LoginOtpChallenge
          challengeId={otpChallengeProps.challengeId}
          availableChannels={otpChallengeProps.availableChannels}
          mustSelectChannel={otpChallengeProps.mustSelectChannel}
          expiresAt={otpChallengeProps.expiresAt}
          attemptsRemaining={otpChallengeProps.attemptsRemaining}
          maxAttempts={otpChallengeProps.maxAttempts}
          resendAvailableInSeconds={otpChallengeProps.resendAvailableInSeconds}
          customerEmail={otpChallengeProps.customerEmail}
          customerPhone={otpChallengeProps.customerPhone}
          customerPhoneCode={otpChallengeProps.customerPhoneCode}
          hideTrustDeviceOption={otpChallengeProps.hideTrustDeviceOption}
          onLoginSuccess={this.handleOtpSuccess}
          onOtpVerifyFailed={this.handleOtpFailure}
        />
      );
    }

    // Show login form
    const isEmailValid = isValidEmail(username);
    const isDisabled =
      loading || (passwordlessEmailLoginEnabled && !isEmailValid);
    const continueButtonStyles = `${styles.buttonContainer} ${
      isDisabled ? "!bg-disabled" : ""
    } !block w-full bg-button text-white text-center py-2 h-11`;
    return (
      <div className="flex flex-col gap-2 loginWithEmail">
        <h1 className="font-montserratMedium text-base leading-5">
          {passwordlessEmailLoginEnabled
            ? "Enter your email"
            : "Enter your email and password"}
        </h1>
        <SimpleInput
          handleInput={e => {
            handleFormInput(e, this, "username");
          }}
          placeholder="Email address *"
          value={username}
          label={defaultLabels.email}
          inputIcon={usernameIcon}
          errorMessage={usernameErrorMessage}
          fieldContainerClass="border-[1px] border-darkElevationPrimary rounded-[40px]"
          fieldClass={`!text-white w-full bg-transparent outline-none h-11 ${styles.inputLightModeOverride}`}
          inputId="email_address"
          hideErrorIcon
        />
        {!passwordlessEmailLoginEnabled && (
          <SimpleInput
            customClass="mt-2"
            handleInput={e => {
              handleFormInput(e, this, "password");
            }}
            placeholder="Password *"
            value={password}
            errorMessage={passwordErrorMessage}
            inputIcon={passwordIcon}
            type="password"
            label={defaultLabels.password}
            fieldContainerClass="border-[1px] border-darkElevationPrimary rounded-[40px]"
            fieldClass={`!text-white w-full bg-transparent outline-none h-11 ${styles.inputLightModeOverride}`}
            inputId="password"
            errorMessageId="login-password-error"
            isRequired
          />
        )}
        <div className={styles.actionsContainer}>
          <Button
            isLoading={loading}
            disabled={isDisabled}
            handleSubmit={isDisabled ? null : () => this.handleSubmit()}
            customContainerStyles={continueButtonStyles}
            label="Continue"
          />
        </div>
        {responseErrorMessage && (
          <p className={styles.hasError}>{responseErrorMessage}</p>
        )}
        {!passwordlessEmailLoginEnabled && (
          <div className={`${styles.forgotPassword} w-full text-center mt-2`}>
            <p>
              <Link to="/reset-password">Forgot Password?</Link>
            </p>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <div className={`${styles.divider} my-4`}>
            <div className="border-t-[1px] border-darkElevationPrimary dark:border-darkElevationSecondary border-solid w-full" />
            <span>or</span>
            <div className="border-t-[1px] border-darkElevationPrimary dark:border-darkElevationSecondary border-solid w-full" />
          </div>

          <button
            onClick={toggleLogin}
            tabIndex={0}
            onKeyDown={e => e.key === "Enter" && toggleLogin()}
            type="button"
            className={`${styles.continueAltMethodButton} bg-light w-full`}
          >
            <p>Continue with Phone</p>
          </button>
        </div>

        {/* SSO CONTAINER */}
        <div className={styles.ssoContainer}>
          <p className={`${styles.privacyTermsCopy} mb-4 text-darkElevationSecondary`}>
            <Link to="/privacy-policy">Privacy Policy</Link>
            &nbsp; & &nbsp;
            <Link to="/terms">Terms and Conditions</Link>
          </p>
        </div>
      </div>
    );
  }
}

LoginWithEmail.propTypes = {
  handleLogin: PropTypes.func.isRequired,
  toggleLogin: PropTypes.func.isRequired,
  setVerificationToken: PropTypes.func.isRequired,
  setVerificationAction: PropTypes.func.isRequired,
  passwordlessEmailLoginEnabled: PropTypes.bool,
};

LoginWithEmail.defaultProps = {
  passwordlessEmailLoginEnabled: false,
};

export const mapStateToProps = state => ({
  passwordlessEmailLoginEnabled:
    state.footerSettings?.passwordlessEmailLoginEnabled || false,
});

export const mapDispatchToProps = dispatch => ({
  setVerificationToken: value =>
    dispatch(elementsActions.setVerificationToken(value)),
  setVerificationAction: value =>
    dispatch(elementsActions.setVerificationAction(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginWithEmail)
